import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { ShieldCheck, Sparkles, Clock4, BadgeCheck } from "lucide-react";

const SERVICES = [
  "Home Cleaning",
  "Office Cleaning",
  "Commercial Cleaning",
  "Construction Cleanup",
];

const TRUST = [
  { icon: ShieldCheck, label: "Insured & Bonded Crews" },
  { icon: Sparkles, label: "Eco-friendly Products" },
  { icon: Clock4, label: "Flexible Same-week Slots" },
  { icon: BadgeCheck, label: "Satisfaction Guarantee" },
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service_type: SERVICES[0],
  address: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.preferred_date) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      toast.success("Estimate request received! We'll reach out shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="pc-section bg-[var(--pc-grey)]">
      <div className="pc-container grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="eyebrow text-[var(--pc-green-hover)]">Free Estimate</div>
          <h2 className="font-display mt-3 text-4xl font-black tracking-tighter sm:text-5xl">
            Tell us about your space. Get a custom quote.
          </h2>
          <p className="mt-5 max-w-md text-base text-black/60">
            Every job is unique. We tailor pricing to the size, condition and
            schedule of your space — no awkward "starting at" gimmicks.
          </p>

          <ul className="mt-10 space-y-4">
            {TRUST.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-base">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-black text-[var(--pc-green)]">
                  <Icon size={18} />
                </span>
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <form
          data-testid="booking-form"
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl border border-black/10 bg-white p-6 shadow-sm md:p-10"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Full Name *">
              <input
                data-testid="booking-input-name"
                required
                value={form.name}
                onChange={update("name")}
                className="pc-input"
                placeholder="Jane Doe"
              />
            </Field>
            <Field label="Phone *">
              <input
                data-testid="booking-input-phone"
                required
                value={form.phone}
                onChange={update("phone")}
                className="pc-input"
                placeholder="(647) 555-1234"
              />
            </Field>
            <Field label="Email *">
              <input
                data-testid="booking-input-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="pc-input"
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Service Type *">
              <select
                data-testid="booking-input-service"
                value={form.service_type}
                onChange={update("service_type")}
                className="pc-input"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Address *" className="md:col-span-2">
              <input
                data-testid="booking-input-address"
                required
                value={form.address}
                onChange={update("address")}
                className="pc-input"
                placeholder="123 King St W, Toronto"
              />
            </Field>
            <Field label="Preferred Date *">
              <input
                data-testid="booking-input-date"
                required
                type="date"
                value={form.preferred_date}
                onChange={update("preferred_date")}
                className="pc-input"
              />
            </Field>
            <Field label="Preferred Time">
              <input
                data-testid="booking-input-time"
                type="time"
                value={form.preferred_time}
                onChange={update("preferred_time")}
                className="pc-input"
              />
            </Field>
            <Field label="Notes / Square Footage / Frequency" className="md:col-span-2">
              <textarea
                data-testid="booking-input-message"
                value={form.message}
                onChange={update("message")}
                className="pc-input min-h-[120px] py-3"
                placeholder="Tell us anything that helps us quote accurately."
              />
            </Field>
          </div>
          <button
            data-testid="booking-submit"
            type="submit"
            disabled={loading}
            className="pc-btn-primary mt-8 w-full justify-center text-base disabled:opacity-60"
          >
            {loading ? "Sending…" : "Request Free Estimate"}
          </button>
          <p className="mt-3 text-center text-xs text-black/50">
            No card needed. We typically reply within a few hours.
          </p>
        </form>
      </div>

      <style>{`
        .pc-input {
          width: 100%;
          border: 1px solid rgba(10,10,10,0.12);
          background: #fff;
          padding: 0.9rem 1rem;
          border-radius: 0.85rem;
          font-size: 0.95rem;
          color: #0a0a0a;
          transition: border-color .2s ease, box-shadow .2s ease;
          font-family: inherit;
        }
        .pc-input:focus {
          outline: none;
          border-color: var(--pc-green);
          box-shadow: 0 0 0 4px rgba(0,230,118,0.18);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
        {label}
      </span>
      {children}
    </label>
  );
}
